import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';

import { CartKeys } from '@config/keys/cart.keys';
import { DoodCartModel } from '@core/models/cart.model';
import { IOrder } from '@core/models/order.model';
import { DoodProductModel } from '@core/models/product.model';
import { ComoAsset, ComoAttachUserResponse, ComoUser } from '@core/models/como.model';
import { DoodUserModel } from '@store/authentication/authentication.model';

import { CartService } from '@core/services/cart/cart.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { OrdersService } from '@core/services/orders/orders.service';
import { BasketService } from '@core/services/basket/basket.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ComoApiService } from '@core/services/api/como/como-api.service';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

import { ComoSsoLoadingModalComponent } from '@shared/modals/como/como-sso-loading-modal/como-sso-loading-modal.component';
import { ComoConfirmUsePointModalComponent } from '@shared/modals/como/como-confirm-use-point-modal/como-confirm-use-point-modal.component';
import { ComoProductsSelectionModalComponent } from '@shared/modals/como/como-products-selection-modal/como-products-selection-modal.component';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { UserService } from '@core/services/user/user.service';
import { EntitiesApiService } from '@core/services/api/entities/entities-api.service';
import { OrderTypeValues } from '@config/values/order.values';
import { ShopKeys } from '@config/keys/shop.keys';
import { DoodEntityType } from '@core/models/entity.model';
import {
  ComoPostAttachRouteDecision,
  hasComoAccountOnMarketplace,
  resolveComoRegistrationStepAfterAttach,
} from '@core/services/como/como-link-registration.util';

@Injectable({
  providedIn: 'root',
})
export class ComoService {
  private comoUser$ = new BehaviorSubject<ComoUser | undefined | null>(undefined);
  private debouncedLoadComoUser$ = new Subject<boolean>();

  constructor(
    private readonly authSelector: AuthStoreSelector,
    private readonly cartSelector: CartStoreSelector,
    private readonly cartService: CartService,
    private readonly basketSelector: BasketStoreSelector,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly ordersService: OrdersService,
    private readonly modalsService: ModalsService,
    private readonly basketService: BasketService,
    private readonly comoApiService: ComoApiService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly shopSelector: ShopStoreSelector,
    private readonly entitiesApiService: EntitiesApiService,
    private readonly authFirebaseService: AuthFirebaseService,
    private readonly userService: UserService,
    private readonly analyticsService: AnalyticsService,
  ) {
    this.debouncedLoadComoUser$
      .pipe(
        debounceTime(50),
        switchMap(() =>
          combineLatest([
            this.authSelector.selectUser$,
            this.marketplaceSelector.selectMarketplaceId$,
            this.cartSelector.selectActive$.pipe(distinctUntilChanged(), throttleTime(5000)),
            // Compare shop id only: the shop entity object is patched often (products, …)
            // and would cancel in-flight get-user-info via switchMap.
            this.shopSelector.selectActive$.pipe(
              map(shop => shop?.[ShopKeys.Id] as string | undefined),
              distinctUntilChanged(),
            ),
          ]),
        ),
        debounceTime(50),
        switchMap(([user, marketplaceId, cart, activeShopId]) => {
          const order = this.ordersService.mapCartToOrderValues(cart as DoodCartModel);
          // API get-user-info expects an order context with at least type + shop/marketplace.
          // On /fidelite without a started journey, cart may be null — fall back to defaults.
          const orderShop = (order as { shop?: string | null } | null)?.shop;
          const knownShopId = orderShop || activeShopId || undefined;
          const isEnabled = this.hasComoAccountOnMarketplace(user, marketplaceId);

          if (!user?.id || !isEnabled) {
            this.comoUser$.next(null);
            return of(null);
          }

          return this.resolveShopIdForComoUserInfo$(marketplaceId, knownShopId).pipe(
            switchMap(shopId => {
              if (!shopId) {
                return of(null);
              }

              const baseOrder = { ...(order ?? {}) } as Record<string, unknown>;
              delete baseOrder['shop'];

              return this.updateComoUser$({
                ...baseOrder,
                type: (order?.type ?? OrderTypeValues.ClickAndCollect) as string,
                multi_shop: ((order as { multi_shop?: boolean | null } | null)?.multi_shop ??
                  false) as boolean,
                products: ((order as { products?: unknown[] } | null)?.products ?? []) as unknown[],
                shop: shopId,
                user: user.id as string,
                marketplace: marketplaceId,
              });
            }),
          );
        }),
      )
      .subscribe();
  }

  loadComoUser(): void {
    this.debouncedLoadComoUser$.next(true);
  }

  getComoUser$(): BehaviorSubject<ComoUser | undefined | null> {
    return this.comoUser$;
  }

  sendPaymentVerificationCode$(): Observable<boolean> {
    return this.cartSelector.selectActive$.pipe(
      take(1),
      map(cart => this.ordersService.mapCartToOrderValues(cart as DoodCartModel)),
      switchMap(cart => this.comoApiService.sendPaymentVerificationCode$(cart)),
      catchError(() => {
        return of(undefined);
      }),
      map(result => !!result?.verification_code_required),
    );
  }

  createComoUser$(
    phoneOrEmail: string,
    legalAccepted = false,
    dateOfBirth: string | null = null,
    marketingAccepted = false,
  ): Observable<DoodUserModel> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      switchMap(marketplaceId =>
        this.comoApiService.createComoUser(
          marketplaceId,
          phoneOrEmail,
          legalAccepted,
          dateOfBirth,
          marketingAccepted,
        ),
      ),
    );
  }

  /**
   * Whether the Como registration modal should close after attach (redirect) vs password step.
   * Logs `[DOOD-3204] ComoService.postAttachRoute` for production diagnosis.
   */
  resolveRegistrationStepAfterAttach(
    response: ComoAttachUserResponse,
    user: DoodUserModel | null | undefined,
    marketplaceId: string,
    allowAnonymousUser: boolean,
  ): ComoPostAttachRouteDecision {
    const decision = resolveComoRegistrationStepAfterAttach(
      response,
      user,
      marketplaceId,
      allowAnonymousUser,
    );
    this.analyticsService.log({
      level: decision.nextStep === 'complete' ? 'info' : 'warn',
      label: '[DOOD-3204] ComoService.postAttachRoute',
      message: {
        next_step: decision.nextStep,
        reason: decision.reason,
        has_custom_token: !!response.custom_token,
        has_access_token: !!response.access_token,
        attach_result: response.result ?? null,
        allow_anonymous_user: allowAnonymousUser,
        is_anonymous: user?.is_anonymous ?? null,
        has_como_metafield: hasComoAccountOnMarketplace(user, marketplaceId),
        has_user: !!user?.id,
      },
    });
    return decision;
  }

  linkComoUser$({
    phoneOrEmail,
    code,
  }: { phoneOrEmail?: string; code?: string } = {}): Observable<ComoAttachUserResponse> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      switchMap(marketplaceId =>
        this.comoApiService.linkComoUser$(marketplaceId, phoneOrEmail, code),
      ),
      switchMap(response => {
        const hasCode = !!code;
        // New guest created by Como attach — store Dood anonymous JWT
        if (response.access_token) {
          this.userService.setDoodAnonymousAccessToken(
            response.access_token,
            response.expires_in ?? 21600,
          );
          this.authFirebaseService.startAnonymousUserLogoutTimer();
          return this.userService.loadUser$().pipe(
            tap(user => this.logComoLinkSession(response, user, hasCode, 'anonymous_jwt')),
            map(() => response),
          );
        }
        if (response.custom_token) {
          return this.authFirebaseService.signInWithCustomToken(response.custom_token).pipe(
            switchMap(() => this.userService.loadUser$()),
            tap(user => {
              this.loadComoUser();
              this.logComoLinkSession(response, user, hasCode, 'custom_token');
            }),
            map(() => response),
          );
        } else if (response.result === true) {
          return this.userService.loadUser$().pipe(
            tap(user => {
              this.loadComoUser();
              this.logComoLinkSession(response, user, hasCode, 'existing_session');
            }),
            map(() => response),
          );
        } else {
          this.logComoLinkSession(response, this.authSelector.selectUser(), hasCode, 'no_session');
          return of(response);
        }
      }),
    );
  }

  private logComoLinkSession(
    response: ComoAttachUserResponse,
    user: DoodUserModel | null | undefined,
    hasCode: boolean,
    sessionKind: 'anonymous_jwt' | 'custom_token' | 'existing_session' | 'no_session',
  ): void {
    this.analyticsService.log({
      level: sessionKind === 'anonymous_jwt' ? 'warn' : 'info',
      label: '[DOOD-3204] ComoService.linkComoUser',
      message: {
        session_kind: sessionKind,
        otp_sent: !!response.otp_sent,
        result: response.result ?? null,
        has_code: hasCode,
        is_anonymous: user?.is_anonymous ?? null,
        has_user: !!user?.id,
      },
    });
  }

  attachUserWithEmail$(): Observable<boolean> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      switchMap(marketplaceId => this.comoApiService.attachUserWithEmail$(marketplaceId)),
    );
  }

  isUserComoAccountEnabledOnMarketplaceLevel$(): Observable<boolean> {
    return combineLatest([
      this.authSelector.selectUser$,
      this.marketplaceSelector.selectMarketplaceId$,
    ]).pipe(map(([user, marketplaceId]) => this.hasComoAccountOnMarketplace(user, marketplaceId)));
  }

  handleSingleSignOn(): void {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (!urlSearchParams.has('temporaryToken')) {
      return;
    }

    const temporaryToken = urlSearchParams.get('temporaryToken');

    if (!temporaryToken) {
      return;
    }

    this.modalsService.open(ComoSsoLoadingModalComponent.handle);
  }

  getErrorMessageFromCode(code: number): string {
    let errorMessage = "Une erreur inconnue s'est produite";
    switch (code) {
      case 103:
        // Phone number already exists
        errorMessage = 'como.create-account.errors.account-already-exists-phone';
        break;
      case 10:
        // Invalid code
        errorMessage = 'como.link-account.errors.invalid-auth-code';
        break;
      case 202:
        errorMessage = 'como.link-account.errors.account-not-found-phone';
        break;
      case 0:
        errorMessage = 'como.errors.generic-error';
        break;
      case 101:
        // Como is already attached to user
        errorMessage = 'como.create-account.errors.user-has-loyalty-attached';
        break;
      case 201:
        // Unable to send verification code
        errorMessage = 'como.link-account.errors.unable-to-send-code';
        break;
      case 102:
        // Phone number is missing
        break;
      case 1:
        // Not logged in
        break;
      case 3:
        // Como not enabled
        errorMessage = 'como.errors.loyalty-not-enabled';
        break;
      default:
        errorMessage = 'como.errors.generic-error';
    }
    return errorMessage;
  }

  confirmUsePointToAddAsset(asset: ComoAsset, _kiosk?: boolean): void {
    let isPointShopAsset = asset.key?.startsWith('ps_');
    if (isPointShopAsset) {
      this.modalsService.open(ComoConfirmUsePointModalComponent.handle, undefined, {
        asset: asset,
      });
    } else {
      this.displayProductsSelectionModal(asset);
    }
  }

  allowedProducts$(externalIds: string[], shopId?: string): Observable<DoodProductModel[]> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      switchMap(marketplaceId =>
        this.comoApiService.allowedProducts$(marketplaceId, externalIds, shopId),
      ),
    );
  }

  displayProductsSelectionModal(asset: ComoAsset): void {
    this.modalsService.open(ComoProductsSelectionModalComponent.handle, undefined, {
      asset: asset,
    });
  }

  addRewardToCart$(asset: ComoAsset): Observable<any> {
    this.cartService.addCoupon({ key: asset?.key });
    return this.orderDispatcher.checkCartIsValid$().pipe(
      take(1),
      switchMap(validate => {
        const basket = this.basketSelector.basket[CartKeys.Id];
        if (asset?.key && basket) {
          return this.basketService.addBasketCoupon$(asset?.key as string);
        } else {
          return of(validate);
        }
      }),
    );
  }

  private hasComoAccountOnMarketplace(
    user: DoodUserModel | null | undefined,
    marketplaceId: string,
  ): boolean {
    return hasComoAccountOnMarketplace(user, marketplaceId);
  }

  private resolveShopIdForComoUserInfo$(
    marketplaceId: string,
    knownShopId?: string,
  ): Observable<string | undefined> {
    if (knownShopId) {
      return of(knownShopId);
    }

    return this.entitiesApiService.get$(marketplaceId).pipe(
      map(entities => {
        const firstShop = entities.find(entity => entity.entity_type === DoodEntityType.Shop);
        return firstShop?.id ?? entities[0]?.id;
      }),
      catchError(() => of(undefined)),
    );
  }

  private updateComoUser$(
    order: Partial<IOrder> & {
      user: string;
      marketplace: string;
      type: string;
      shop: string;
      multi_shop?: boolean;
      products?: unknown[];
    },
  ): Observable<ComoUser | undefined | null> {
    return this.comoApiService.getComoUser(order).pipe(
      catchError(() => {
        return of(null);
      }),
      tap(comoUser => {
        this.comoUser$.next(comoUser);
      }),
    );
  }
}
